import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { TabBar } from '../components/TabBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DialogTopBar } from '../components/DialogTopBar';
import { toast } from 'sonner';
import { Plus, ListChecks, Edit, Trash2, Loader2, ChevronDown, ChevronUp, Upload, FileDown, X, MessageSquare, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const CriteriaPage = () => {
    const { getAuthHeaders, API, isAdmin } = useAuth();
    const [criteria, setCriteria] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [editingCriterion, setEditingCriterion] = useState(null);
    const [expandedCriterion, setExpandedCriterion] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState('grading');

    // Criterion Comments tab state
    const [forms, setForms] = useState([]);
    const [formComments, setFormComments] = useState([]);
    const [selectedFormId, setSelectedFormId] = useState(null);
    const [expandedFormSections, setExpandedFormSections] = useState({});
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [uploadingComments, setUploadingComments] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);

    const defaultRatingDescriptions = [
        { score: 0, label: 'Not Assessed', description: 'Not observed or not applicable' },
        { score: 1, label: 'Unsatisfactory', description: '' },
        { score: 2, label: 'Below Standard', description: '' },
        { score: 3, label: 'Industry Standard', description: '' },
        { score: 4, label: 'Above Standard', description: '' },
        { score: 5, label: 'Masterful', description: '' }
    ];

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        max_points: 5,
        rating_descriptions: defaultRatingDescriptions
    });

    useEffect(() => {
        fetchCriteria();
    }, []);

    const fetchCriteria = async () => {
        try {
            const response = await axios.get(`${API}/criteria`, { headers: getAuthHeaders() });
            setCriteria(response.data);
        } catch (error) {
            console.error('Failed to fetch criteria:', error);
            toast.error('Failed to load criteria');
        } finally {
            setLoading(false);
        }
    };

    const fetchForms = async () => {
        try {
            const res = await axios.get(`${API}/admin/assessment-forms`, { headers: getAuthHeaders() });
            setForms(res.data);
            if (res.data.length > 0 && !selectedFormId) {
                setSelectedFormId(res.data[0].id);
                fetchFormComments(res.data[0].id);
            }
        } catch (e) { console.error('Failed to fetch forms:', e); }
    };

    const fetchFormComments = async (formId) => {
        if (!formId) return;
        setCommentsLoading(true);
        try {
            const res = await axios.get(`${API}/admin/form-comments/${formId}`, { headers: getAuthHeaders() });
            setFormComments(res.data);
        } catch (e) { setFormComments([]); }
        finally { setCommentsLoading(false); }
    };

    useEffect(() => {
        if (activeTab === 'comments') fetchForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const openModal = (criterion = null) => {
        if (criterion) {
            setEditingCriterion(criterion);
            setFormData({
                name: criterion.name,
                description: criterion.description || '',
                max_points: criterion.max_points,
                rating_descriptions: criterion.rating_descriptions || defaultRatingDescriptions
            });
        } else {
            setEditingCriterion(null);
            setFormData({
                name: '',
                description: '',
                max_points: 5,
                rating_descriptions: [...defaultRatingDescriptions]
            });
        }
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.name) {
            toast.error('Please enter a criterion name');
            return;
        }

        setSaving(true);
        try {
            if (editingCriterion) {
                await axios.put(`${API}/criteria/${editingCriterion.id}`, formData, { headers: getAuthHeaders() });
                toast.success('Criterion updated successfully');
            } else {
                await axios.post(`${API}/criteria`, formData, { headers: getAuthHeaders() });
                toast.success('Criterion created successfully');
            }
            
            setModalOpen(false);
            fetchCriteria();
        } catch (error) {
            console.error('Failed to save criterion:', error);
            toast.error(error.response?.data?.detail || 'Failed to save criterion');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (criterion) => {
        if (!window.confirm(`Are you sure you want to delete "${criterion.name}"?`)) return;
        
        try {
            await axios.delete(`${API}/criteria/${criterion.id}`, { headers: getAuthHeaders() });
            toast.success('Criterion deleted successfully');
            fetchCriteria();
        } catch (error) {
            console.error('Failed to delete criterion:', error);
            toast.error('Failed to delete criterion');
        }
    };

    const updateRatingDescription = (score, field, value) => {
        setFormData(prev => ({
            ...prev,
            rating_descriptions: prev.rating_descriptions.map(rd =>
                rd.score === score ? { ...rd, [field]: value } : rd
            )
        }));
    };

    const toggleExpand = (criterionId) => {
        setExpandedCriterion(expandedCriterion === criterionId ? null : criterionId);
    };

    const downloadTemplate = () => {
        const wb = XLSX.utils.book_new();
        
        // Template data with example
        const templateData = [
            ['Criterion Name', 'Description', 'Score 0 Label', 'Score 0 Feedback', 'Score 1 Label', 'Score 1 Feedback', 'Score 2 Label', 'Score 2 Feedback', 'Score 3 Label', 'Score 3 Feedback', 'Score 4 Label', 'Score 4 Feedback', 'Score 5 Label', 'Score 5 Feedback'],
            ['Knife Skills', 'Cutting technique, safety, and consistency', 'Not Assessed', 'Not observed or not applicable', 'Unsatisfactory', 'Dangerous technique; highly irregular cuts', 'Below Standard', 'Dangerous; irregular cuts; high waste', 'Industry Standard', 'Uniform cuts; safe handling of knives', 'Above Standard', 'High precision; consistent speed', 'Masterful', 'Perfect uniformity; exceptional speed and yield'],
            ['Sanitation', 'Food safety and hygiene practices', 'Not Assessed', 'Not observed or not applicable', 'Unsatisfactory', 'Major breaches; cross-contamination observed', 'Below Standard', 'Poor hygiene; messy apron/station', 'Industry Standard', 'Standard safety followed; clean station', 'Above Standard', 'Proactive hygiene; excellent temp control', 'Masterful', 'Gold standard; anticipates all safety risks']
        ];
        
        const ws = XLSX.utils.aoa_to_sheet(templateData);
        
        // Set column widths
        ws['!cols'] = [
            { wch: 20 }, // Criterion Name
            { wch: 40 }, // Description
            { wch: 15 }, { wch: 40 }, // Score 0
            { wch: 15 }, { wch: 40 }, // Score 1
            { wch: 15 }, { wch: 40 }, // Score 2
            { wch: 15 }, { wch: 40 }, // Score 3
            { wch: 15 }, { wch: 40 }, // Score 4
            { wch: 15 }, { wch: 40 }  // Score 5
        ];
        
        XLSX.utils.book_append_sheet(wb, ws, 'Criteria Template');
        XLSX.writeFile(wb, 'criteria_upload_template.xlsx');
        toast.success('Template downloaded');
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

            // Skip header row
            const dataRows = rows.slice(1);
            let created = 0;
            let updated = 0;
            let errors = [];

            for (let i = 0; i < dataRows.length; i++) {
                const row = dataRows[i];
                if (!row[0]) continue; // Skip empty rows

                const name = String(row[0]).trim();
                const description = row[1] ? String(row[1]).trim() : '';

                // Build rating descriptions from columns
                const rating_descriptions = [];
                for (let score = 0; score <= 5; score++) {
                    const labelIdx = 2 + (score * 2);
                    const feedbackIdx = 3 + (score * 2);
                    rating_descriptions.push({
                        score: score,
                        label: row[labelIdx] ? String(row[labelIdx]).trim() : getDefaultLabel(score),
                        description: row[feedbackIdx] ? String(row[feedbackIdx]).trim() : ''
                    });
                }

                const criterionData = {
                    name,
                    description,
                    max_points: 5,
                    rating_descriptions
                };

                try {
                    // Check if criterion exists by name
                    const existing = criteria.find(c => c.name.toLowerCase() === name.toLowerCase());
                    
                    if (existing) {
                        await axios.put(`${API}/criteria/${existing.id}`, criterionData, { headers: getAuthHeaders() });
                        updated++;
                    } else {
                        await axios.post(`${API}/criteria`, criterionData, { headers: getAuthHeaders() });
                        created++;
                    }
                } catch (err) {
                    errors.push(`Row ${i + 2}: ${err.response?.data?.detail || err.message}`);
                }
            }

            toast.success(`Upload complete: ${created} created, ${updated} updated`);
            if (errors.length > 0) {
                console.warn('Upload errors:', errors);
            }
            
            setUploadModalOpen(false);
            fetchCriteria();
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error('Failed to process file');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const getDefaultLabel = (score) => {
        const labels = ['Not Assessed', 'Unsatisfactory', 'Below Standard', 'Industry Standard', 'Above Standard', 'Masterful'];
        return labels[score] || '';
    };

    const getScoreColor = (score) => {
        const colors = [
            'bg-gray-100 text-gray-600',
            'bg-red-100 text-red-700',
            'bg-orange-100 text-orange-700',
            'bg-yellow-100 text-yellow-700',
            'bg-blue-100 text-blue-700',
            'bg-green-100 text-green-700'
        ];
        return colors[score] || 'bg-gray-100 text-gray-600';
    };

    // Criterion Comments helpers
    const selectedForm = forms.find(f => f.id === selectedFormId);
    const selectedFormSections = selectedForm?.form_data?.sections || [];

    const getCommentsForSection = (sectionId) => {
        return formComments.filter(c => c.section_id === sectionId || !c.section_id);
    };

    const getUnlinkedComments = () => {
        return formComments.filter(c => !c.criterion_id);
    };

    const handleCommentsUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selectedFormId) return;
        setUploadingComments(true);
        setUploadResult(null);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${API}/admin/form-comments/${selectedFormId}/upload`, fd, {
                headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
            });
            setUploadResult(res.data);
            toast.success(res.data.message);
            fetchFormComments(selectedFormId);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Upload failed');
        } finally { setUploadingComments(false); e.target.value = ''; }
    };

    const downloadCommentsTemplate = async () => {
        if (!selectedFormId) return;
        try {
            const res = await axios.get(`${API}/admin/form-comments/${selectedFormId}/template`, { headers: getAuthHeaders(), responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a'); link.href = url;
            link.setAttribute('download', 'criterion_comments_template.xlsx');
            document.body.appendChild(link); link.click(); link.remove();
        } catch (e) { toast.error('Failed to download template'); }
    };

    const deleteFormComment = async (commentId) => {
        try {
            await axios.delete(`${API}/admin/form-comments/${commentId}`, { headers: getAuthHeaders() });
            setFormComments(prev => prev.filter(c => c.id !== commentId));
            toast.success('Comment deleted');
        } catch (e) { toast.error('Failed to delete'); }
    };

    if (!isAdmin()) {
        return (
            <Layout>
                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <ListChecks className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                    <h3 className="text-base font-semibold text-gray-700 mb-1">Admin Access Required</h3>
                    <p className="text-gray-500">Only administrators can manage grading criteria</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="criteria-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">Criteria</h1>
                        <p className="text-gray-500 mt-1">Manage grading scales and predefined criterion comments</p>
                    </div>
                </div>

                {/* Tabs */}
                <TabBar
                    tabs={[
                        { id: 'grading', label: 'Grading Criteria', icon: ListChecks },
                        { id: 'comments', label: 'Criterion Comments', icon: MessageSquare }
                    ]}
                    active={activeTab} onChange={setActiveTab} testIdPrefix="tab" />

                {activeTab === 'grading' && (
                    <div className="space-y-6 page-enter">
                        <div className="flex items-center gap-3 justify-end">
                            <Button variant="outline" onClick={() => setUploadModalOpen(true)} data-testid="upload-criteria-btn">
                                <Upload className="w-4 h-4 mr-2" /> Upload XLSX
                            </Button>
                            <Button onClick={() => openModal()} data-testid="add-criterion-btn" >
                                <Plus className="w-4 h-4 mr-2" /> Add Criterion
                            </Button>
                        </div>

                {/* Criteria Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy mx-auto" />
                        </div>
                    ) : criteria.length > 0 ? (
                        <div className="divide-y divide-gray-100">
                            {criteria.map((criterion) => (
                                <div key={criterion.id} data-testid={`criterion-row-${criterion.id}`}>
                                    {/* Main Row */}
                                    <div 
                                        className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={() => toggleExpand(criterion.id)}
                                    >
                                        <div className="flex items-center gap-4 flex-1">
                                            <div className="w-10 h-10 bg-ica-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <ListChecks className="w-5 h-5 text-ica-gold" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold text-gray-900">{criterion.name}</h3>
                                                {criterion.description && (
                                                    <p className="text-sm text-gray-500 truncate">{criterion.description}</p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                <span className="px-2 py-1 bg-ica-navy/10 text-ica-navy text-xs font-medium rounded">
                                                    Max: {criterion.max_points} pts
                                                </span>
                                                {criterion.rating_descriptions?.length > 0 && (
                                                    <span className="text-xs text-gray-400">
                                                        {criterion.rating_descriptions.length} rating levels
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 ml-4">
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                onClick={(e) => { e.stopPropagation(); openModal(criterion); }}
                                                data-testid={`edit-criterion-${criterion.id}`}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                onClick={(e) => { e.stopPropagation(); handleDelete(criterion); }}
                                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                data-testid={`delete-criterion-${criterion.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                            {expandedCriterion === criterion.id ? (
                                                <ChevronUp className="w-5 h-5 text-gray-400" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-gray-400" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Expanded Rating Scale */}
                                    {expandedCriterion === criterion.id && criterion.rating_descriptions && (
                                        <div className="px-6 pb-6 bg-gray-50/50">
                                            <div className="ml-14">
                                                <h4 className="text-sm font-semibold text-gray-700 mb-3">Rating Scale & Feedback</h4>
                                                <div className="grid gap-2">
                                                    {criterion.rating_descriptions.map((rd) => (
                                                        <div 
                                                            key={rd.score}
                                                            className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100"
                                                        >
                                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0 ${getScoreColor(rd.score)}`}>
                                                                {rd.score}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium text-gray-900">{rd.label}</p>
                                                                <p className="text-sm text-gray-500">{rd.description || 'No feedback description'}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <Button 
                                                    variant="outline" 
                                                    size="sm" 
                                                    className="mt-3"
                                                    onClick={() => openModal(criterion)}
                                                >
                                                    <Edit className="w-4 h-4 mr-2" />
                                                    Edit Rating Scale
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-10">
                            <ListChecks className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                            <h3 className="text-base font-semibold text-gray-700 mb-1">No criteria yet</h3>
                            <p className="text-gray-500 mb-4">Create grading criteria or upload from a template</p>
                            <div className="flex items-center justify-center gap-3">
                                <Button variant="outline" onClick={() => setUploadModalOpen(true)}>
                                    <Upload className="w-4 h-4 mr-2" />
                                    Upload XLSX
                                </Button>
                                <Button onClick={() => openModal()} >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add Criterion
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                    </div>
                )}

                {/* Criterion Comments Tab */}
                {activeTab === 'comments' && (
                    <div className="space-y-5" data-testid="comments-tab">
                        {/* Form selector + upload */}
                        <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
                            <div className="flex items-center gap-4 flex-wrap">
                                <div className="flex-1 min-w-[200px]">
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Select Form Template</label>
                                    <select
                                        value={selectedFormId || ''}
                                        onChange={e => { setSelectedFormId(e.target.value); fetchFormComments(e.target.value); }}
                                        className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm focus:border-ica-gold focus:ring-1 focus:ring-ica-gold"
                                        data-testid="form-selector"
                                    >
                                        {forms.map(f => (
                                            <option key={f.id} value={f.id}>{f.form_name || 'Untitled Form'}</option>
                                        ))}
                                        {forms.length === 0 && <option value="">No forms available</option>}
                                    </select>
                                </div>
                                <div className="flex items-end gap-2 pt-5">
                                    <Button variant="outline" size="sm" onClick={downloadCommentsTemplate} disabled={!selectedFormId} data-testid="download-comments-template">
                                        <Download className="w-4 h-4 mr-1" /> Template
                                    </Button>
                                    <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed cursor-pointer transition-all text-sm ${
                                        uploadingComments ? 'border-gray-200 bg-gray-100' : 'border-gray-300 hover:border-ica-gold hover:bg-ica-gold/5'
                                    }`}>
                                        {uploadingComments ? <><Loader2 className="w-4 h-4 animate-spin text-gray-400" />Uploading...</> : <><Upload className="w-4 h-4 text-gray-500" />Upload Comments</>}
                                        <input type="file" accept=".xlsx,.xls,.csv" onChange={handleCommentsUpload} className="hidden" disabled={uploadingComments || !selectedFormId} data-testid="comments-upload-input" />
                                    </label>
                                </div>
                            </div>
                            {uploadResult && (
                                <p className="text-xs text-green-600 font-medium bg-green-50 rounded-lg px-3 py-2">{uploadResult.created} comments uploaded successfully</p>
                            )}
                            <p className="text-xs text-gray-400">
                                Template columns: Form → Section → Comment → Type (Positive/Negative/General).
                                Comments are linked at the section level and will appear during grading for any assessment using this form.
                            </p>
                        </div>

                        {/* Comments by section & criterion */}
                        {commentsLoading ? (
                            <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy mx-auto" /></div>
                        ) : selectedFormSections.length > 0 ? (
                            <div className="space-y-4">
                                <p className="text-sm text-gray-500">{formComments.length} comments linked to <span className="font-medium text-gray-700">{selectedForm?.form_name}</span></p>
                                {selectedFormSections.map(section => {
                                    const isExpanded = expandedFormSections[section.id] !== false;
                                    const sectionCriteria = section.criteria || [];
                                    const sComments = getCommentsForSection(section.id);
                                    return (
                                        <div key={section.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`comment-section-${section.id}`}>
                                            <button onClick={() => setExpandedFormSections(prev => ({ ...prev, [section.id]: !isExpanded }))}
                                                className="w-full flex items-center justify-between px-5 py-3 bg-[#1e3a5f] text-white hover:bg-[#253f5f] transition-colors">
                                                <span className="text-sm font-bold uppercase tracking-wide">{section.title}</span>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-white/60">{sComments.length} comments · {sectionCriteria.length} criteria</span>
                                                    {isExpanded ? <ChevronUp className="w-4 h-4 text-white/50" /> : <ChevronDown className="w-4 h-4 text-white/50" />}
                                                </div>
                                            </button>
                                            {isExpanded && (
                                                <div>
                                                    {/* Criteria list */}
                                                    <div className="divide-y divide-gray-50">
                                                        {sectionCriteria.map(c => (
                                                            <div key={c.id} className="px-5 py-2">
                                                                <p className="text-sm text-gray-700">{c.label}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {/* Section-level comments */}
                                                    <div className="px-5 py-3 bg-gray-50/80 border-t border-gray-100">
                                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Section Comments</p>
                                                        {sComments.length > 0 ? (
                                                            <div className="flex gap-1.5 flex-wrap">
                                                                {sComments.map(pc => (
                                                                    <span key={pc.id} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${
                                                                        pc.type === 'positive' ? 'bg-green-50 text-green-700 border border-green-200'
                                                                        : pc.type === 'negative' ? 'bg-red-50 text-red-600 border border-red-200'
                                                                        : 'bg-gray-50 text-gray-600 border border-gray-200'
                                                                    }`} data-testid={`form-comment-${pc.id}`}>
                                                                        {pc.label}
                                                                        <button onClick={(e) => { e.stopPropagation(); deleteFormComment(pc.id); }} className="text-gray-400 hover:text-red-500 ml-0.5">
                                                                            <X className="w-3 h-3" />
                                                                        </button>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-gray-400 italic">No comments for this section</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {/* Unlinked comments */}
                                {getUnlinkedComments().length > 0 && (
                                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                        <div className="px-5 py-3 bg-gray-100 border-b border-gray-200">
                                            <span className="text-sm font-medium text-gray-600">Unlinked Comments ({getUnlinkedComments().length})</span>
                                        </div>
                                        <div className="px-5 py-3 flex gap-1.5 flex-wrap">
                                            {getUnlinkedComments().map(pc => (
                                                <span key={pc.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                                    {pc.label}
                                                    <button onClick={() => deleteFormComment(pc.id)} className="text-gray-400 hover:text-red-500 ml-0.5">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p className="text-gray-500">
                                    {forms.length === 0 ? 'No assessment forms available. Create a form first.' : 'Select a form template above to manage its criterion comments.'}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Add/Edit Modal */}
                <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="criterion-modal">
                        <DialogTopBar title={<>{editingCriterion ? 'Edit Criterion' : 'Add New Criterion'}</>}>
    <Button size="sm" variant="outline" onClick={() => setModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button size="sm" 
                                onClick={handleSubmit}
                                disabled={saving}
                                data-testid="save-criterion-btn"
                                
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    editingCriterion ? 'Update Criterion' : 'Create Criterion'
                                )}
                            </Button>
</DialogTopBar>
                        
                        <div className="space-y-6 py-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Criterion Name *</Label>
                                    <Input
                                        data-testid="criterion-name-input"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g., Knife Skills"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Max Points</Label>
                                    <Input
                                        data-testid="criterion-points-input"
                                        type="number"
                                        min={1}
                                        max={10}
                                        value={formData.max_points}
                                        onChange={(e) => setFormData({ ...formData, max_points: parseInt(e.target.value) || 5 })}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Description</Label>
                                <Textarea
                                    data-testid="criterion-description-input"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Describe what this criterion evaluates..."
                                    rows={2}
                                />
                            </div>

                            {/* Rating Scale Editor */}
                            <div className="space-y-3">
                                <Label className="text-base font-semibold">Rating Scale & Feedback</Label>
                                <p className="text-sm text-gray-500">Define the label and feedback description for each score level</p>
                                
                                <div className="space-y-3">
                                    {formData.rating_descriptions.map((rd) => (
                                        <div 
                                            key={rd.score}
                                            className="p-4 border border-gray-200 rounded-lg bg-gray-50/50"
                                        >
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${getScoreColor(rd.score)}`}>
                                                    {rd.score}
                                                </div>
                                                <Input
                                                    value={rd.label}
                                                    onChange={(e) => updateRatingDescription(rd.score, 'label', e.target.value)}
                                                    placeholder="Label (e.g., Industry Standard)"
                                                    className="flex-1"
                                                    data-testid={`rating-label-${rd.score}`}
                                                />
                                            </div>
                                            <Textarea
                                                value={rd.description}
                                                onChange={(e) => updateRatingDescription(rd.score, 'description', e.target.value)}
                                                placeholder="Feedback description for this score level..."
                                                rows={2}
                                                className="w-full"
                                                data-testid={`rating-desc-${rd.score}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Upload Modal */}
                <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
                    <DialogContent data-testid="upload-criteria-modal">
                        <DialogHeader>
                            <DialogTitle>Upload Criteria</DialogTitle>
                        </DialogHeader>
                        
                        <div className="py-4">
                            <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                <p className="text-gray-600 mb-2">Upload an Excel file (.xlsx)</p>
                                <p className="text-sm text-gray-500 mb-4">
                                    Include criterion name, description, and rating scale with feedback for each level (0-5)
                                </p>
                                
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    id="criteria-file-upload"
                                    data-testid="criteria-file-input"
                                />
                                
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <Button 
                                        variant="outline"
                                        onClick={downloadTemplate}
                                        data-testid="download-criteria-template-btn"
                                    >
                                        <FileDown className="w-4 h-4 mr-2" />
                                        Download Template
                                    </Button>
                                    <label htmlFor="criteria-file-upload">
                                        <Button 
                                            asChild
                                            disabled={uploading}
                                            className="bg-ica-gold hover:bg-ica-gold-dark cursor-pointer"
                                        >
                                            <span data-testid="select-criteria-file-btn">
                                                {uploading ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Uploading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Upload className="w-4 h-4 mr-2" />
                                                        Select File
                                                    </>
                                                )}
                                            </span>
                                        </Button>
                                    </label>
                                </div>
                            </div>

                            <div className="mt-4 p-4 bg-ica-gold/5 border border-ica-gold/20 rounded-lg">
                                <h4 className="font-medium text-ica-navy mb-2">Template Format</h4>
                                <p className="text-sm text-gray-600">
                                    The template includes columns for:
                                </p>
                                <ul className="text-sm text-gray-600 mt-2 space-y-1">
                                    <li>• <strong>Criterion Name</strong> - Name of the criterion</li>
                                    <li>• <strong>Description</strong> - What the criterion evaluates</li>
                                    <li>• <strong>Score 0-5 Labels</strong> - Label for each rating level</li>
                                    <li>• <strong>Score 0-5 Feedback</strong> - Description for each rating level</li>
                                </ul>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

export default CriteriaPage;
